import { grey } from '@mui/material/colors';
import Student from '../../../model/Student';
import { Checkbox, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CustomTable from '../CustomTable';
import { useContext, useEffect, useRef, useState } from 'react';
import { getSecondAStageStudents, updateSecondAStageResult } from '../../../api/content-api';
import { loginPage } from '../../../store/fixed-routing';
import AuthContext from '../../../store/auth-context';
import SnackbarContext from '../../../store/snackbar-context';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../ui/Loader';
import ActionButtons from './ActionButtons';
import Result from '../../../model/Result';
import OlympicsContext from '../../../store/olympics-context';
import { DEFAULT_API_ERROR, DEFAULT_AUTH_ERROR } from '../../../utils/auth-tools';
import { decodeBase64Utf8 } from '../../../utils/string-tools';
import { filter } from '../StagesTable';
import ContactLinks from '../ContactLinks';
import ProtocolVerificationDialog from './ProtocolVerificationDialog';

const SecondAStageTable = () => {
	const [allStudents, setAllStudents] = useState([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student>();
	const [isLoading, setIsLoading] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);
	const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
	const { logout, token, isAdmin, isSecretary, id } = useContext(AuthContext);
	const { olympics, districtFilterId, searchFilterValue, refreshTablesTrigger, setIsRefreshing } =
		useContext(OlympicsContext);
	const isRefreshingRef = useRef(false);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();
	const editable = olympics && (isAdmin || !olympics?.secondAStagePublished);

	const filterStudents = (list: Student[]) => {
		let filtered = filter(list, districtFilterId);

		if (searchFilterValue) {
			filtered = filtered.filter((s) => {
				const matchesSearch = (value: string | undefined) =>
					value && value.toLowerCase().includes(searchFilterValue.toLowerCase());

				return (
					matchesSearch(s.firstName) ||
					matchesSearch(s.lastName) ||
					matchesSearch(s.email) ||
					matchesSearch(s.school?.name) ||
					matchesSearch(s.district?.name) ||
					s.teachers?.some((t: any) => matchesSearch(t.firstName) || matchesSearch(t.lastName))
				);
			});
		}

		return filtered;
	};

	useEffect(() => {
		setDisplayedStudents(filterStudents(allStudents));
	}, [districtFilterId, searchFilterValue, allStudents]);

	const downloadData = (isRefresh = false) => {
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}
		getSecondAStageStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
				} else {
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
	};

	const handleSaveFirstStageResult = () => {
		setIsUpdating(true);
		const data = {
			id: editedStudent?.id,
			secondAStageTopic: editedStudent?.result.secondAStageTopic,
			secondAOneStagePoints: editedStudent?.result.secondAOneStagePoints,
			secondATwoStagePoints: editedStudent?.result.secondATwoStagePoints,
		};
		updateSecondAStageResult(token, data)
			.then((response) => {
				setIsUpdating(false);
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
					setEditedStudent(undefined);
					setMsg({ msg: 'Dane ucznia zostały zaktualizowane', severity: 'success' });
					const warningEncoded = response.headers?.['x-olympics-warning-b64'];
					const warningFallback = response.headers?.['x-olympics-warning'];
					if (warningEncoded || warningFallback) {
						const warning = warningEncoded ? decodeBase64Utf8(warningEncoded) : warningFallback;
						setMsg({ msg: warning, severity: 'warning' });
					}
				} else {
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				setIsUpdating(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			downloadData(false);
		} else {
			downloadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger, token]);

	const handlePointsChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = Number(event.target.value);
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const handleTextChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = event?.target?.value ? event.target.value.toUpperCase() : '';
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const renderPointsField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				type={'number'}
				value={editedStudent.result[accessor]}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				onChange={(event) => handlePointsChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const renderSumField = (i: Student) => {
		let value = 0;
		if (editedStudent?.id === i.id) {
			value = editedStudent.result.secondAOneStagePoints + editedStudent.result.secondATwoStagePoints;
		} else {
			value = i.result.secondAOneStagePoints + i.result.secondATwoStagePoints;
		}
		value = isNaN(value) ? 0 : value;
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{value}
			</Typography>
		);
	};

	const renderEditableField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				value={editedStudent.result[accessor]}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				inputProps={{ maxLength: 2 }}
				onChange={(event) => handleTextChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const data = (input: any) => {
		const contactColumns = isSecretary ? [{ header: 'Kontakt', accessor: 'contact', align: 'left' }] : [];
		let additionalColumns: any[] = [];

		if (isAdmin || isSecretary) {
			additionalColumns = [
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
			];
		}

		return {
			columns: [
				{ header: 'Lp.', accessor: 'index', align: 'left', width: '70px' },
				{ header: 'Identyfikator', accessor: 'id', align: 'center', width: '150px' },
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				...additionalColumns,
				...contactColumns,
				{ header: 'Szkoła', accessor: 'school', align: 'left' },
				{ header: 'Okręg', accessor: 'district', align: 'left', width: '140px' },
				{ header: 'Nauczyciel', accessor: 'teachers', align: 'left', width: '160px' },
				{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
				{
					header: 'Specjalizacja',
					accessor: 'specialization',
					width: '100px',
					align: 'center',
					bgColor: grey[100],
				},
				{ header: 'Temat', accessor: 'subject', align: 'center', editable: editable },
				{ header: 'Praca literacka', accessor: 'pointsA', width: '120px', align: 'center', editable: editable },
				{ header: 'Test', accessor: 'pointsB', width: '100px', align: 'center', editable: editable },
				{ header: 'Suma', accessor: 'sum', width: '100px', align: 'center' },
				{ header: 'Zakwalifikowany', accessor: 'qualified', width: '170px', align: 'center' },
				{ header: '', accessor: 'action', align: 'center', width: '100px', nosort: true, editable: editable },
			],

			rows: input
				? input.map((i: Student, index: number) => {
						return {
							index: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{index + 1}
								</Typography>
							),
							id: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district.id.toString().padStart(4, '0') + '-' + i.id.toString().padStart(6, '0')}
								</Typography>
							),
							personalTitle: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.personalTitle}
								</Typography>
							),
							contact: isSecretary && <ContactLinks student={i} />,
							firstName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.firstName}
								</Typography>
							),
							lastName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.lastName}
								</Typography>
							),
							school: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.school.name}, {i.school.cityWithCountry}
								</Typography>
							),
							district: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district?.name || ''}
								</Typography>
							),
							teachers: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{(i.teachers || [])
										.map((t) => `${t.firstName} ${t.lastName}`.trim())
										.filter(Boolean)
										.join(', ')}
								</Typography>
							),
							schoolClass: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.schoolClassText && i.schoolClassText.trim() !== '' ? i.schoolClassText : String(i.schoolClass)}
								</Typography>
							),
							specialization: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialization.name}
								</Typography>
							),
							subject: renderEditableField(i, 'secondAStageTopic'),
							pointsA: renderPointsField(i, 'secondAOneStagePoints'),
							pointsB: renderPointsField(i, 'secondATwoStagePoints'),
							sum: renderSumField(i),
							qualified: (
								<Checkbox
									checked={i.result.secondAStageQualified}
									disabled={true}
								/>
							),
							action: (
								<ActionButtons
									student={i}
									disabled={!editable}
									editedStudent={editedStudent}
									inProgress={isUpdating}
									handleSave={handleSaveFirstStageResult}
									handleClose={() => setEditedStudent(undefined)}
									handleEdit={() => setEditedStudent(i)}
								/>
							),
						};
					})
				: [],
		};
	};
	const footerButtons = isAdmin ? (
		<Tooltip title='Weryfikacja protokołu (Etap IIA)'>
			<IconButton
				size='small'
				sx={{ color: 'white' }}
				onClick={() => setVerificationDialogOpen(true)}
			>
				<FileUploadIcon fontSize='small' />
			</IconButton>
		</Tooltip>
	) : null;

	return (
		<>
			{isLoading ? (
				<Loader />
			) : (
				<CustomTable
					data={data(displayedStudents)}
					footerLeft={footerButtons}
				/>
			)}
			<ProtocolVerificationDialog
				open={verificationDialogOpen}
				onClose={() => setVerificationDialogOpen(false)}
				stageKey='IIA'
				title='Weryfikacja protokołu (Etap IIA)'
				students={displayedStudents}
			/>
		</>
	);
};

export default SecondAStageTable;
